import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import '../styles/EnrichirPage.css';
import type { EnrichFile, PrecedentUploadStatus } from '../shared/precedent-types';
import { PRECEDENT_STATUS_LABELS } from '../shared/precedent-types';
import {
  initPrecedentUpload,
  confirmPrecedentUpload,
  processPrecedent,
  getPrecedentsBatchStatus,
  getPrecedentsList,
  deletePrecedent,
  uploadToS3,
} from '../services/apiClient';
import type { PrecedentListItem } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { ConfirmationModal } from './ConfirmationModal';

// Constants
const MAX_CONCURRENT_UPLOADS = 3;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const POLL_INTERVAL = 2000; // 2 seconds

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

/**
 * Validate if a file is PDF or DOCX and within size limits
 */
function validateFile(file: File): { valid: boolean; type: 'pdf' | 'docx' | null; error?: string } {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const isValidExtension = ACCEPTED_EXTENSIONS.includes(extension);
  const isValidMime = ACCEPTED_MIME_TYPES.includes(file.type);

  if (!isValidExtension && !isValidMime) {
    return { valid: false, type: null, error: 'Format non supporté' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, type: null, error: 'Fichier trop volumineux (max 50 Mo)' };
  }

  const type = extension === '.pdf' || file.type === 'application/pdf' ? 'pdf' : 'docx';
  return { valid: true, type };
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Compute SHA-256 hash of a file
 */
async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Get the relative path from a File object (available when uploaded via folder)
 */
function getRelativePath(file: File): string | undefined {
  // webkitRelativePath is available when using webkitdirectory attribute
  const relativePath = (file as any).webkitRelativePath;
  return relativePath && relativePath !== file.name ? relativePath : undefined;
}

/**
 * Recursively read files from a directory entry, preserving relative paths
 */
async function readDirectoryEntry(
  entry: FileSystemDirectoryEntry,
  basePath: string = ''
): Promise<{ file: File; relativePath: string }[]> {
  const results: { file: File; relativePath: string }[] = [];
  const reader = entry.createReader();

  const readEntries = (): Promise<FileSystemEntry[]> => {
    return new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  };

  let entries = await readEntries();
  while (entries.length > 0) {
    for (const e of entries) {
      const currentPath = basePath ? `${basePath}/${e.name}` : e.name;
      if (e.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (e as FileSystemFileEntry).file(resolve, reject);
        });
        results.push({ file, relativePath: currentPath });
      } else if (e.isDirectory) {
        const subResults = await readDirectoryEntry(e as FileSystemDirectoryEntry, currentPath);
        results.push(...subResults);
      }
    }
    entries = await readEntries();
  }

  return results;
}

export const EnrichirPage: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<EnrichFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // Upload queue management
  const uploadQueueRef = useRef<string[]>([]);
  const activeUploadsRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Ref to access latest files without stale closure
  const latestFilesRef = useRef<EnrichFile[]>(files);
  latestFilesRef.current = files;

  // Cleanup on unmount: abort all uploads and clear polling
  useEffect(() => {
    return () => {
      // Abort all in-progress uploads
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();

      // Clear queue and active uploads
      uploadQueueRef.current = [];
      activeUploadsRef.current.clear();

      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * Update a specific file's state
   */
  const updateFile = useCallback((id: string, updates: Partial<EnrichFile>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  /**
   * Add files to the list with deduplication
   */
  const addFiles = useCallback((
    fileList: FileList | File[] | { file: File; relativePath: string }[]
  ) => {
    const newFiles: EnrichFile[] = [];

    // Normalize input to array of { file, relativePath }
    const items: { file: File; relativePath: string | undefined }[] = [];

    if (fileList instanceof FileList) {
      // FileList from input element
      for (let i = 0; i < fileList.length; i++) {
        const f = fileList[i];
        items.push({ file: f, relativePath: getRelativePath(f) });
      }
    } else if (Array.isArray(fileList) && fileList.length > 0) {
      // Check if it's an array of File or array of { file, relativePath }
      const firstItem = fileList[0];
      if (firstItem instanceof File) {
        // Array of File objects
        for (const f of fileList as File[]) {
          items.push({ file: f, relativePath: getRelativePath(f) });
        }
      } else {
        // Array of { file, relativePath }
        for (const item of fileList as { file: File; relativePath: string }[]) {
          items.push({ file: item.file, relativePath: item.relativePath });
        }
      }
    }

    for (const { file, relativePath } of items) {
      const validation = validateFile(file);
      if (!validation.valid || !validation.type) continue;

      // Check for duplicates by name + size + relativePath (local dedup)
      // Real deduplication by hash happens server-side
      // Check against existing files
      const isDuplicateInExisting = files.some(
        (f) => f.name === file.name &&
               f.size === file.size &&
               f.relativePath === relativePath
      );
      // Also check against files being added in this batch
      const isDuplicateInBatch = newFiles.some(
        (f) => f.name === file.name &&
               f.size === file.size &&
               f.relativePath === relativePath
      );
      if (isDuplicateInExisting || isDuplicateInBatch) continue;

      newFiles.push({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        relativePath,
        size: file.size,
        type: validation.type,
        status: 'pending',
      });
    }

    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [files]);

  /**
   * Remove a file from the list
   */
  const removeFile = useCallback((id: string) => {
    // Cancel any ongoing upload
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }

    // Remove from queue
    uploadQueueRef.current = uploadQueueRef.current.filter(fid => fid !== id);
    activeUploadsRef.current.delete(id);

    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  /**
   * Process a single file upload
   */
  const uploadFile = useCallback(async (fileId: string) => {
    // Use latestFilesRef to avoid stale closure
    const fileData = latestFilesRef.current.find(f => f.id === fileId);
    if (!fileData) return;

    const abortController = new AbortController();
    abortControllersRef.current.set(fileId, abortController);

    try {
      // Step 1: Compute hash
      updateFile(fileId, { status: 'hashing' });
      const hash = await computeFileHash(fileData.file);
      updateFile(fileId, { hash });

      if (abortController.signal.aborted) return;

      // Step 2: Init upload (check for duplicates)
      const contentType = fileData.type === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      const initResponse = await initPrecedentUpload({
        filename: fileData.name,
        relative_path: fileData.relativePath,
        file_size: fileData.size,
        content_type: contentType,
        file_hash: hash,
      });

      if (!initResponse.success || !initResponse.data) {
        throw new Error(initResponse.error || 'Erreur lors de l\'initialisation');
      }

      if (abortController.signal.aborted) return;

      // Check if file already exists (duplicate)
      if (initResponse.data.exists && initResponse.data.existing_precedent) {
        updateFile(fileId, {
          status: 'analyzed',
          isDuplicate: true,
          existingPrecedent: {
            id: initResponse.data.existing_precedent.id,
            filename: initResponse.data.existing_precedent.filename,
            relativePath: initResponse.data.existing_precedent.relative_path,
            status: initResponse.data.existing_precedent.status,
            createdAt: initResponse.data.existing_precedent.created_at,
          },
        });
        return;
      }

      // Step 3: Upload to S3
      updateFile(fileId, {
        status: 'uploading',
        uploadProgress: 0,
        storageKey: initResponse.data.storage_key,
      });

      const uploadResult = await uploadToS3(
        initResponse.data.upload_url!,
        fileData.file,
        (percent) => updateFile(fileId, { uploadProgress: percent }),
        abortController.signal,
        contentType
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Erreur lors de l\'upload');
      }

      if (abortController.signal.aborted) return;

      // Step 4: Confirm upload
      const confirmResponse = await confirmPrecedentUpload({
        storage_key: initResponse.data.storage_key!,
        filename: fileData.name,
        relative_path: fileData.relativePath,
        file_hash: hash,
        content_type: contentType,
        file_size: fileData.size,
      });

      if (!confirmResponse.success || !confirmResponse.data) {
        throw new Error(confirmResponse.error || 'Erreur lors de la confirmation');
      }

      if (abortController.signal.aborted) return;

      // Step 5: Start processing
      updateFile(fileId, {
        status: 'processing',
        precedentId: confirmResponse.data.id,
      });

      const processResponse = await processPrecedent(confirmResponse.data.id);

      if (!processResponse.success) {
        throw new Error(processResponse.error || 'Erreur lors du traitement');
      }

      // Processing continues in background - polling will update status

    } catch (error: any) {
      if (error.name === 'AbortError' || abortController.signal.aborted) {
        // Upload was cancelled - don't update status
        return;
      }

      updateFile(fileId, {
        status: 'error',
        errorMessage: error.message || 'Une erreur est survenue',
      });
    } finally {
      abortControllersRef.current.delete(fileId);
    }
  }, [updateFile]);

  /**
   * Process the upload queue
   */
  const processQueue = useCallback(() => {
    while (
      uploadQueueRef.current.length > 0 &&
      activeUploadsRef.current.size < MAX_CONCURRENT_UPLOADS
    ) {
      const fileId = uploadQueueRef.current.shift()!;
      activeUploadsRef.current.add(fileId);

      uploadFile(fileId).finally(() => {
        activeUploadsRef.current.delete(fileId);
        processQueue();
      });
    }
  }, [uploadFile]);

  /**
   * Poll for processing status of files
   */
  const pollStatus = useCallback(async () => {
    const processingFiles = files.filter(f => f.status === 'processing' && f.precedentId);

    if (processingFiles.length === 0) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const ids = processingFiles.map(f => f.precedentId!);

    try {
      const response = await getPrecedentsBatchStatus(ids);

      if (response.success && response.data) {
        for (const item of response.data.precedents) {
          const file = processingFiles.find(f => f.precedentId === item.id);
          if (!file) continue;

          if (item.status === 'analyzed') {
            updateFile(file.id, { status: 'analyzed' });
          } else if (item.status === 'error') {
            updateFile(file.id, {
              status: 'error',
              errorMessage: item.error_message || 'Erreur de traitement',
            });
          }
          // 'pending' or 'processing' - continue polling
        }
      }
    } catch (error) {
      console.error('Polling error:', error);
    }
  }, [files, updateFile]);

  /**
   * Start polling when files are in processing state
   */
  useEffect(() => {
    const hasProcessing = files.some(f => f.status === 'processing');

    if (hasProcessing && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(pollStatus, POLL_INTERVAL);
    } else if (!hasProcessing && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [files, pollStatus]);

  /**
   * Update isProcessing state based on file statuses
   */
  useEffect(() => {
    const hasActive = files.some(f =>
      ['hashing', 'uploading', 'processing'].includes(f.status)
    );
    setIsProcessing(hasActive);
  }, [files]);

  /**
   * Handle form submission - start uploading all pending files
   */
  const handleSubmit = useCallback(() => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0 || isProcessing) return;

    // Add all pending files to the queue
    uploadQueueRef.current = pendingFiles.map(f => f.id);

    // Start processing the queue
    processQueue();
  }, [files, isProcessing, processQueue]);

  /**
   * Retry a failed file
   */
  const retryFile = useCallback((id: string) => {
    updateFile(id, {
      status: 'pending',
      errorMessage: undefined,
      uploadProgress: undefined,
    });
  }, [updateFile]);

  // Drag & Drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropzoneRef.current && !dropzoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const droppedFiles: { file: File; relativePath: string }[] = [];

    // Process items to handle both files and directories
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
          if (entry.isFile) {
            const file = item.getAsFile();
            if (file) {
              droppedFiles.push({ file, relativePath: file.name });
            }
          } else if (entry.isDirectory) {
            const dirResults = await readDirectoryEntry(
              entry as FileSystemDirectoryEntry,
              entry.name
            );
            droppedFiles.push(...dirResults);
          }
        } else {
          const file = item.getAsFile();
          if (file) {
            droppedFiles.push({ file, relativePath: file.name });
          }
        }
      }
    }

    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, [addFiles]);

  // File input handlers
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files;
    if (inputFiles && inputFiles.length > 0) {
      addFiles(inputFiles);
    }
    e.target.value = '';
  }, [addFiles]);

  const handleFolderInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files;
    if (inputFiles && inputFiles.length > 0) {
      // Files from folder input have webkitRelativePath set
      addFiles(inputFiles);
    }
    e.target.value = '';
  }, [addFiles]);

  const handleFileButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFolderButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    folderInputRef.current?.click();
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDropzoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  /**
   * Get the status badge content for a file
   */
  const renderFileStatus = (file: EnrichFile) => {
    const statusClass = file.status;
    const label = PRECEDENT_STATUS_LABELS[file.status];

    return (
      <div className={`enrichir-file-status ${statusClass}`}>
        {(file.status === 'hashing' || file.status === 'processing') && (
          <span className="enrichir-file-status-spinner" />
        )}
        {file.status === 'uploading' && (
          <>
            <span className="enrichir-file-status-spinner" />
            <span>{file.uploadProgress ?? 0}%</span>
          </>
        )}
        {file.status === 'analyzed' && (
          <svg className="enrichir-status-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )}
        {file.status === 'error' && (
          <svg className="enrichir-status-icon error" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
        {file.status !== 'uploading' && <span>{label}</span>}
        {file.isDuplicate && <span className="enrichir-file-duplicate">(existant)</span>}
      </div>
    );
  };

  /**
   * Render file actions based on status
   */
  const renderFileActions = (file: EnrichFile) => {
    if (file.status === 'error') {
      return (
        <>
          <button
            type="button"
            className="enrichir-file-retry"
            onClick={() => retryFile(file.id)}
            title="Réessayer"
            aria-label={`Réessayer ${file.name}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
          <button
            type="button"
            className="enrichir-file-remove"
            onClick={() => removeFile(file.id)}
            title="Supprimer"
            aria-label={`Supprimer ${file.name}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>
        </>
      );
    }

    if (file.status === 'pending' || file.status === 'analyzed') {
      return (
        <button
          type="button"
          className="enrichir-file-remove"
          onClick={() => removeFile(file.id)}
          disabled={isProcessing && file.status !== 'analyzed'}
          title="Supprimer"
          aria-label={`Supprimer ${file.name}`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      );
    }

    // For hashing, uploading, processing - show cancel button
    return (
      <button
        type="button"
        className="enrichir-file-cancel"
        onClick={() => removeFile(file.id)}
        title="Annuler"
        aria-label={`Annuler ${file.name}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    );
  };

  // Calculate counts for the header
  const pendingCount = files.filter(f => f.status === 'pending').length;
  const completedCount = files.filter(f => f.status === 'analyzed').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="enrichir-page">
      <div className="enrichir-container">
        {/* Header Section */}
        <div className="enrichir-header">
          <div className="enrichir-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12" />
              <path d="M5 10l7-7 7 7" />
              <rect x="4" y="17" width="16" height="4" rx="1" />
            </svg>
          </div>
          <h1 className="enrichir-title">Enrichir</h1>
          <p className="enrichir-subtitle">
            Ajoutez des fichiers ou dossiers de contrats pour enrichir le clausier de votre entreprise
          </p>
        </div>

        {/* Confidentiality Banner */}
        <div className="enrichir-confidentiality">
          <svg className="enrichir-confidentiality-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <p className="enrichir-confidentiality-text">
            Vos contrats sont strictement confidentiels et utilisés uniquement pour enrichir la bibliothèque de clauses de votre entreprise. Ils ne sont jamais utilisés pour l'entrainement de nos modèles.
          </p>
        </div>

        {/* Dropzone - hidden when files exist */}
        {files.length === 0 && (
          <div
            ref={dropzoneRef}
            className={`enrichir-dropzone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              onChange={handleFileInputChange}
              className="enrichir-file-input"
            />
            <input
              ref={folderInputRef}
              type="file"
              // @ts-expect-error webkitdirectory is not in the types
              webkitdirectory=""
              multiple
              onChange={handleFolderInputChange}
              className="enrichir-file-input"
            />

            {/* Clickable area for icon + text only */}
            <div
              className="enrichir-dropzone-clickable"
              onClick={handleDropzoneClick}
              onKeyDown={handleDropzoneKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Cliquer pour choisir des fichiers"
            >
              <div className="enrichir-dropzone-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>

              <p className="enrichir-dropzone-text">
                Déposer ou <span className="enrichir-dropzone-link">choisir</span> les documents à analyser
              </p>
            </div>

            {/* Buttons outside the clickable area */}
            <div className="enrichir-dropzone-buttons">
              <button
                type="button"
                className="enrichir-dropzone-btn"
                onClick={handleFileButtonClick}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
                Choisir des fichiers
              </button>
              <button
                type="button"
                className="enrichir-dropzone-btn"
                onClick={handleFolderButtonClick}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                Choisir un dossier
              </button>
            </div>

            <div className="enrichir-formats">
              <span className="enrichir-formats-label">Formats acceptés :</span>
              <div className="enrichir-format-tags">
                <span className="enrichir-format-tag">PDF</span>
                <span className="enrichir-format-tag">DOCX</span>
              </div>
            </div>
          </div>
        )}

        {/* Files List Section */}
        {files.length > 0 && (
          <div className="enrichir-files-section">
            <div className="enrichir-files-card">
              <div className="enrichir-files-header">
                <span className="enrichir-files-count">
                  {files.length} {files.length === 1 ? 'document' : 'documents'}
                  {completedCount > 0 && (
                    <span className="enrichir-count-success"> ({completedCount} terminé{completedCount > 1 ? 's' : ''})</span>
                  )}
                  {errorCount > 0 && (
                    <span className="enrichir-count-error"> ({errorCount} erreur{errorCount > 1 ? 's' : ''})</span>
                  )}
                </span>
                <button
                  type="button"
                  className="enrichir-add-more-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Ajouter
                </button>
              </div>

              <div className="enrichir-files-list">
                {files.map((file) => (
                  <div key={file.id} className={`enrichir-file-item ${file.status}`}>
                    <div className={`enrichir-file-icon ${file.type}`}>
                      {file.type === 'pdf' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                          <path d="M10 9H8v6h2" />
                          <path d="M14 9h-2v6" />
                          <path d="M18 9h-2v6h2" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                          <path d="M16 13H8" />
                          <path d="M16 17H8" />
                          <path d="M10 9H8" />
                        </svg>
                      )}
                    </div>

                    <div className="enrichir-file-info">
                      <div className="enrichir-file-name">{file.name}</div>
                      {file.relativePath && file.relativePath !== file.name && (
                        <div className="enrichir-file-path" title={file.relativePath}>
                          {file.relativePath}
                        </div>
                      )}
                      <div className="enrichir-file-meta">
                        <span className="enrichir-file-size">{formatFileSize(file.size)}</span>
                        {file.errorMessage && (
                          <span className="enrichir-file-error" title={file.errorMessage}>
                            {file.errorMessage}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Progress bar for uploading status */}
                    {file.status === 'uploading' && (
                      <div className="enrichir-file-progress">
                        <div
                          className="enrichir-file-progress-fill"
                          style={{ width: `${file.uploadProgress ?? 0}%` }}
                        />
                      </div>
                    )}

                    {renderFileStatus(file)}

                    <div className="enrichir-file-actions">
                      {renderFileActions(file)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Section */}
            <div className="enrichir-submit-section">
              <button
                type="button"
                className="enrichir-submit-btn"
                onClick={handleSubmit}
                disabled={pendingCount === 0 || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="enrichir-file-status-spinner" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    Envoyer {pendingCount > 0 ? `${pendingCount} document${pendingCount > 1 ? 's' : ''}` : 'les documents'}
                  </>
                )}
              </button>
            </div>

            {/* Hidden file inputs when files exist */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              onChange={handleFileInputChange}
              className="enrichir-file-input"
            />
            <input
              ref={folderInputRef}
              type="file"
              // @ts-expect-error webkitdirectory is not in the types
              webkitdirectory=""
              multiple
              onChange={handleFolderInputChange}
              className="enrichir-file-input"
            />
          </div>
        )}
      </div>
    </div>
  );
};
